import { horizonRoas, sortBy } from './campaigns-logic.js';
import { largestRemainderPct } from './format.js';
import { markerValueLabel } from './marker-labels.js';
export const PORTRAIT_VIEWS = ['all', 'one', 'accounts'];
export function readView(v) {
    return PORTRAIT_VIEWS.includes(v) ? v : 'all';
}
/** Порог «крупной группы» и надёжного значения на карточке. */
export const MIN_N = 30;
export function valueRows(key, values) {
    const total = Object.values(values).reduce((s, g) => s + g.purchases, 0);
    const entries = Object.entries(values);
    const pcts = largestRemainderPct(entries.map(([, g]) => g.purchases));
    return entries.map(([raw, group], i) => ({
        raw,
        label: markerValueLabel(key, raw),
        group,
        share: total > 0 ? group.purchases / total : 0,
        sharePct: pcts[i],
    }));
}
/**
 * «Крупные группы»: значения от `MIN_N` покупок и строка «Остальные» без
 * прогноза; «все значения» — каждое значение.
 */
export function bucketRows(rows, all, minN = MIN_N) {
    if (all)
        return { rows: [...rows], rest: null };
    const big = rows.filter((r) => r.group.purchases >= minN);
    const small = rows.filter((r) => r.group.purchases < minN);
    if (small.length === 0)
        return { rows: big, rest: null };
    return {
        rows: big,
        rest: {
            count: small.length,
            purchases: small.reduce((s, r) => s + r.group.purchases, 0),
            spendMinor: small.reduce((s, r) => s + r.group.spendMinorFull, 0),
            share: small.reduce((s, r) => s + r.share, 0),
            sharePct: small.reduce((s, r) => s + r.sharePct, 0),
        },
    };
}
/** Колонки, видимые по умолчанию (в этом порядке). */
export const MARKER_BASE_COLUMNS = [
    'value',
    'share',
    'spend',
    'purchases',
    'cpp',
    'breakeven',
    'roas',
    'chance',
    'verdict',
    // Справочные — после вывода: при 1440 главное видно без прокрутки.
    'safe',
    'fact',
];
export const MARKER_COLUMN_LABEL = {
    value: 'Значение',
    share: 'Доля покупок',
    spend: 'Расход',
    purchases: 'Покупки',
    cpp: 'Цена',
    breakeven: 'В ноль до',
    safe: 'Уверенно до',
    fact: 'Вернулось к сегодня',
    roas: 'Вернётся на 1 $',
    chance: 'Шанс',
    verdict: 'Вывод',
    h60: '60 дн',
    h90: '90 дн',
};
/** Колонки «Одного признака»: «Значение» закреплено и всегда видно, 60/90 дн скрыты. */
export const MARKER_COLUMN_DEFS = [...MARKER_BASE_COLUMNS, 'h60', 'h90'].map((id) => ({
    id,
    label: MARKER_COLUMN_LABEL[id],
    ...(id === 'value' ? { defaultPin: 'left', hideable: false } : {}),
    ...(id === 'h60' || id === 'h90' ? { defaultVisible: false } : {}),
    ...(id === 'roas' ? { minWidth: 140 } : {}),
}));
export const MARKER_SORT_KEYS = MARKER_COLUMN_DEFS.map((d) => d.id);
/** Колонки, у которых в строке «Остальные» есть своё значение. */
export const REST_ROW_VALUE_COLUMNS = new Set([
    'value',
    'share',
    'spend',
    'purchases',
]);
/**
 * Раскладка строки «Остальные» по видимым колонкам — по id, а не по позиции:
 * перестановка и скрытие колонок её не ломают. Колонки со значением
 * (`REST_ROW_VALUE_COLUMNS`) — свои ячейки; соседние остальные склеиваются
 * в одну (`colSpan` = их число). Закреплённая колонка не склеивается
 * (широкая закреплённая ячейка закрыла бы таблицу при прокрутке). Пояснение
 * «мало покупок…» — в первом незакреплённом блоке, нет такого — в первом.
 */
export function restRowSegments(visible, hasValue) {
    const out = [];
    const spans = [];
    // Блок, к которому можно приклеить следующую колонку.
    let open = null;
    for (const c of visible) {
        if (hasValue(c.id)) {
            open = null;
            out.push({ kind: 'value', id: c.id });
            continue;
        }
        const pinned = c.pin !== null;
        if (open && !pinned) {
            open.ids.push(c.id);
            continue;
        }
        const seg = { kind: 'span', ids: [c.id], note: false };
        out.push(seg);
        spans.push({ seg, pinned });
        open = pinned ? null : seg;
    }
    const target = spans.find((s) => !s.pinned) ?? spans[0];
    if (target)
        target.seg.note = true;
    return out;
}
export const DEFAULT_MARKER_SORT = { key: 'purchases', dir: 'desc' };
const VERDICT_RANK = { yes: 3, unclear: 2, no: 1 };
export function markerSortValue(r, key) {
    const g = r.group;
    switch (key) {
        case 'value':
            return r.label.toLocaleLowerCase('ru');
        case 'share':
            return r.share;
        case 'spend':
            return g.spendMinorFull;
        case 'purchases':
            return g.purchases;
        case 'cpp':
            return g.cpp;
        case 'breakeven':
            return g.cppBreakevenMinor;
        case 'safe':
            return g.cppSafeMinor;
        case 'fact':
            return g.factRoas;
        case 'roas':
            return g.answer.roas3;
        case 'chance':
            return g.answer.verdict === null ? null : g.answer.p;
        case 'verdict':
            return g.answer.verdict === null ? null : VERDICT_RANK[g.answer.verdict];
        case 'h60':
            return horizonRoas(g, '60');
        case 'h90':
            return horizonRoas(g, '90');
    }
}
export function sortValueRows(rows, sort) {
    return sortBy(rows, (r) => markerSortValue(r, sort.key), sort.dir);
}
/** Страница списка: номер прижимается к 1…N. */
export function paginate(rows, page, size) {
    const pageCount = Math.max(1, Math.ceil(rows.length / size));
    const p = Math.min(Math.max(1, Math.trunc(page) || 1), pageCount);
    return { page: p, pageCount, total: rows.length, rows: rows.slice((p - 1) * size, p * size) };
}
/** Признак доступен и есть значения. */
export function markerValues(m) {
    return m && m.available ? m.values : null;
}
const dollars = (minor) => typeof minor === 'number' && Number.isFinite(minor) ? Math.round(minor) / 100 : null;
const round2 = (x) => typeof x === 'number' && Number.isFinite(x) ? Math.round(x * 100) / 100 : null;
/** CSV «Одного признака»: все значения признака, видимые колонки. */
export function markerCsvColumns(visible, verdictText) {
    return visible.map((key) => {
        const money = ['spend', 'cpp', 'breakeven', 'safe'].includes(key);
        const percent = key === 'share' || key === 'chance';
        const header = `${MARKER_COLUMN_LABEL[key]}${money ? ', $' : percent ? ', %' : ''}`;
        const value = (r) => {
            const g = r.group;
            switch (key) {
                case 'value':
                    return r.label;
                case 'share':
                    return Math.round(r.share * 1000) / 10;
                case 'spend':
                    return dollars(g.spendMinorFull);
                case 'purchases':
                    return g.purchases;
                case 'cpp':
                    return dollars(g.cpp);
                case 'breakeven':
                    return dollars(g.cppBreakevenMinor);
                case 'safe':
                    return dollars(g.cppSafeMinor);
                case 'fact':
                    return round2(g.factRoas);
                case 'roas':
                    return round2(g.answer.roas3);
                case 'chance':
                    return g.answer.verdict === null || g.answer.p === null
                        ? null
                        : Math.round(g.answer.p * 100);
                case 'verdict':
                    return verdictText(g);
                case 'h60':
                    return round2(horizonRoas(g, '60'));
                case 'h90':
                    return round2(horizonRoas(g, '90'));
            }
        };
        return { header, value };
    });
}
//# sourceMappingURL=portrait-logic.js.map